import React, { useState, useEffect } from 'react';
import { Box, Divider, Grid, Stack, useMediaQuery } from '@mui/material';
import useTrackPageView from '../../hooks/useTrackPageView';
import { PAGE_TYPE } from '../../constants/gaEnums';
import { useTheme } from '@mui/material/styles';
import { useSearchParams } from 'react-router-dom';
import NewsCard from '../../components/card/NewsCard';
import NewsVerticalCard from '../../components/card/NewsVerticalCard';
import ProgressBar from '../../components/common/ProgressBar';
import Pagination from '../../components/common/Pagination';
import InFeedAd from '../../components/ad/InFeedAd';
import InFeedBoxAd from '../../components/ad/InFeedBoxAd';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { newsApi } from '../../api/news';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../../components/layout/PageLayout';
import NoData from '../../components/common/NoData';
import { useUserStore } from '../../stores/userStore';
import CreateButton from '../../components/button/CreateButton';
import PageTitle from '../../components/common/PageTitle';
import ViewModeToggle from '../../components/common/ViewModeToggle';
import SEO from '../../components/common/SEO';
import { SEO_DESCRIPTIONS } from '../../constants/seoDescriptions';
import { Helmet } from 'react-helmet-async';
import { SITE_URL } from '../../utils/env';
import { ViewListRounded, ViewStreamRounded } from '@mui/icons-material';

// 10줄마다 페이지네이션 (list/xs: 1열, md/lg grid: 4열)
const ITEMS_PER_PAGE = { single: 10, grid: 40 };
// 5줄마다 광고
const ITEMS_PER_AD = { single: 5, grid: 20 };

const News = () => {
  useTrackPageView({ pageType: PAGE_TYPE.NEWS_MAIN });
  const theme = useTheme();
  const isAdmin = useUserStore((state) => state.isAdmin);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('newsViewMode') || 'list');

  const handleChangeViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem('newsViewMode', mode);
    setSearchParams({ page: '1' });
  };

  // breakpoint 감지
  const isXs = useMediaQuery(theme.breakpoints.down('md'));
  const isGridMultiCol = !isXs && viewMode === 'grid';
  const itemsPerPage = isGridMultiCol ? ITEMS_PER_PAGE.grid : ITEMS_PER_PAGE.single;
  const itemsPerAd = isGridMultiCol ? ITEMS_PER_AD.grid : ITEMS_PER_AD.single;

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  const fetchNews = async (page = 1, size) => {
    setLoading(true);
    try {
      const response = await newsApi.getAllNews(page, size);
      setNews(response.data);
      if (response.meta && response.meta.pagination) {
        setPagination(response.meta.pagination);
      }
    } catch (err) {
      console.error('Error fetching news:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const page = parseInt(searchParams.get('page')) || 1;
    fetchNews(page, itemsPerPage);
  }, [searchParams, itemsPerPage]);

  const handlePageChange = (event, newPage) => {
    setSearchParams({ page: newPage.toString() });
  };

  return (
    <PageLayout>
      <SEO title="뉴스" description={SEO_DESCRIPTIONS.NEWS} url={`${SITE_URL}/news`} />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Postpop', item: SITE_URL },
              { '@type': 'ListItem', position: 2, name: '뉴스' },
            ],
          })}
        </script>
      </Helmet>
      {news.length > 0 && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'ItemList',
              itemListElement: news
                .filter((item) => item.imageUrl)
                .map((item, index) => ({
                  '@type': 'ListItem',
                  name: item.title,
                  image: item.imageUrl,
                  url: item.slug
                    ? `${SITE_URL}/news/${item.slug}`
                    : `${SITE_URL}/news/${item.id}`,
                  position: index + 1,
                })),
            })}
          </script>
        </Helmet>
      )}
      {/* 타이틀 + 생성 버튼 + xs 보기 아이콘 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: { xs: '16px', md: '40px' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: '16px', md: '20px', lg: '30px' },
          }}
        >
          <PageTitle>뉴스</PageTitle>
          {isAdmin && (
            <CreateButton
              text="뉴스"
              onClick={() => navigate('/news/create')}
              sx={{ fontSize: 14, fontWeight: 500, padding: { xs: '10px 12px', md: '12px 16px' } }}
            />
          )}
        </Box>
        {/* xs ViewMode 아이콘 */}
        {isXs && (
          <ViewModeToggle
            viewMode={viewMode}
            onChangeViewMode={handleChangeViewMode}
            modes={[
              { value: 'list', Icon: ViewListRounded },
              { value: 'grid', Icon: ViewStreamRounded },
            ]}
          />
        )}
      </Box>

      <ProgressBar isLoading={loading}>
        {news.length === 0 ? (
          <NoData />
        ) : (
          <>
            {/* md/lg viewMode 아이콘 */}
            {!isXs && (
              <ViewModeToggle
                viewMode={viewMode}
                onChangeViewMode={handleChangeViewMode}
                sx={{ mb: { md: '20px', lg: '30px' } }}
              />
            )}
            <SidebarLayout pageType={PAGE_TYPE.NEWS_MAIN}>
              {/* 카드 목록 */}
              {viewMode === 'list' ? (
                <Stack spacing={{ xs: '15px', md: '20px', lg: '30px' }}>
                  {news.map((item, index) => (
                    <React.Fragment key={`${item.id}-${index}`}>
                      <Box>
                        <NewsCard news={item} showContent />
                        <Divider
                          sx={{
                            borderColor: theme.palette.grey.line,
                            mt: { xs: '15px', md: '20px', lg: '30px' },
                          }}
                        />
                      </Box>
                      {(index + 1) % itemsPerAd === 0 && index < news.length - 1 && <InFeedAd pageType={PAGE_TYPE.NEWS_MAIN} />}
                    </React.Fragment>
                  ))}
                </Stack>
              ) : (
                <Box sx={{ overflow: 'hidden' }}>
                  <Grid
                    container
                    columnSpacing={{ md: '16px', lg: '20px' }}
                    rowSpacing={{ xs: '30px', md: '40px' }}
                  >
                    {news.map((item, index) => (
                      <React.Fragment key={`${item.id}-${index}`}>
                        <Grid item xs={12} md={3}>
                          <NewsVerticalCard news={item} />
                        </Grid>
                        {(index + 1) % itemsPerAd === 0 && index < news.length - 1 && (
                          <Grid item xs={12}>
                            <InFeedBoxAd pageType={PAGE_TYPE.NEWS_MAIN} />
                          </Grid>
                        )}
                      </React.Fragment>
                    ))}
                  </Grid>
                </Box>
              )}
              {/*페이지네이션*/}
              <Pagination
                count={pagination.totalPages}
                page={pagination.currentPage}
                onChange={handlePageChange}
              />
            </SidebarLayout>
          </>
        )}
      </ProgressBar>
    </PageLayout>
  );
};

export default News;
